/* ENSSA — RiskTransitionSankey.
   2-column Sankey (term-pair comparison) with segmented pair selector,
   unknown always shown when present, flow hover/dim/tooltip. Pure SVG. */

import React from "react";
import { ArrowRight, X } from "lucide-react";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { useTracker } from "@/hooks/useTracker";
import { domainRiskLabel } from "@/lib/copy";
import { screeningPeriodLabel, screeningPeriodList } from "@/lib/screening-periods";
import type { RiskOrUnknown, TransitionRow } from "@/types";

type SankeyTermKey = 't1' | 't3' | 't4';

const SANKEY_COLORS: Record<RiskOrUnknown, string> = {
  high:      'var(--chart-risk-high)',
  moderate:  'var(--chart-risk-moderate)',
  low:       'var(--chart-risk-low)',
  unknown:   'var(--chart-risk-insufficient)',
  more_info: 'var(--chart-risk-insufficient)',
};
/* Mobile labels still flow through the shared vocabulary helper; when the
   active copy includes a trailing "risk", we trim that suffix so the
   narrow mobile columns keep the same semantics without overflowing. */
function compactDomainRiskLabel(band: RiskOrUnknown): string {
  const label = domainRiskLabel(band);
  return label.endsWith(' risk') ? label.slice(0, -5) : label;
}
const TERM_LABEL: Record<SankeyTermKey, string> = {
  t1: screeningPeriodLabel('t1'),
  t3: screeningPeriodLabel('t3'),
  t4: screeningPeriodLabel('t4'),
};

interface FlowPathArgs {
  fromX: number;
  toX: number;
  fromY: number;
  toY: number;
  height: number;
}

function generateFlowPath({ fromX, toX, fromY, toY, height: h }: FlowPathArgs): string {
  const cx1 = fromX + (toX - fromX) * 0.4;
  const cx2 = fromX + (toX - fromX) * 0.6;
  return `M ${fromX} ${fromY} C ${cx1} ${fromY}, ${cx2} ${toY}, ${toX} ${toY} L ${toX} ${toY+h} C ${cx2} ${toY+h}, ${cx1} ${fromY+h}, ${fromX} ${fromY+h} Z`;
}

type BandKey = 'high' | 'moderate' | 'low' | 'unknown';
type BandCounts = Record<BandKey, number>;

interface LayoutNode {
  band: BandKey;
  y: number;
  h: number;
  n: number;
}

function layoutNodes(counts: BandCounts, bands: readonly BandKey[], totalH: number, gap: number): LayoutNode[] {
  const total = bands.reduce((s, b) => s + (counts[b] || 0), 0) || 1;
  const nodes: LayoutNode[] = []; let y = 0;
  for (const b of bands) {
    const n = counts[b] || 0;
    if (!n) continue;
    const h = Math.max(4, (n / total) * (totalH - gap * (bands.length - 1)));
    nodes.push({ band: b, y, h, n });
    y += h + gap;
  }
  return nodes;
}

interface RawFlow {
  from: BandKey;
  to: BandKey;
  n: number;
  fromTerm: SankeyTermKey;
  toTerm: SankeyTermKey;
}

interface BuiltFlow {
  key: string;
  from: BandKey;
  to: BandKey;
  n: number;
  fromTerm: SankeyTermKey;
  toTerm: SankeyTermKey;
  path: string;
  color: string;
}

function buildFlows(leftNodes: LayoutNode[], rightNodes: LayoutNode[], flows: RawFlow[], leftX: number, rightX: number): BuiltFlow[] {
  const offL: Partial<Record<BandKey, number>> = {};
  const offR: Partial<Record<BandKey, number>> = {};
  leftNodes.forEach(n  => { offL[n.band] = 0; });
  rightNodes.forEach(n => { offR[n.band] = 0; });
  const nodeL: Partial<Record<BandKey, LayoutNode>> = {};
  const nodeR: Partial<Record<BandKey, LayoutNode>> = {};
  leftNodes.forEach(n  => { nodeL[n.band] = n; });
  rightNodes.forEach(n => { nodeR[n.band] = n; });

  // Walk flows in a fixed band order so they tile cleanly on both sides.
  // Order matches the visual stack: On Track (low) on top → Low (moderate) →
  // Very Low (high) → Pending (unknown) at the bottom.
  const BAND_ORDER: readonly BandKey[] = ['low','moderate','high','unknown'];
  const sorted = [...flows].sort((a, b) => {
    const ai = BAND_ORDER.indexOf(a.to), bi = BAND_ORDER.indexOf(b.to);
    const af = BAND_ORDER.indexOf(a.from), bf = BAND_ORDER.indexOf(b.from);
    return af !== bf ? af - bf : ai - bi;
  });

  const result: BuiltFlow[] = [];
  for (const f of sorted) {
    const lNode = nodeL[f.from];
    const rNode = nodeR[f.to];
    if (!lNode || !rNode || f.n <= 0) continue;
    // height = proportion of FROM node's own students (not total)
    const h = Math.max(1, (f.n / lNode.n) * lNode.h);
    const fromY = lNode.y + (offL[f.from] ?? 0);
    const toY   = rNode.y + (offR[f.to]   ?? 0);
    offL[f.from] = (offL[f.from] ?? 0) + h;
    offR[f.to]   = (offR[f.to]   ?? 0) + h;
    result.push({
      key: `${f.from}-${f.to}-${f.fromTerm}-${f.toTerm}`,
      from: f.from, to: f.to, n: f.n,
      fromTerm: f.fromTerm, toTerm: f.toTerm,
      path: generateFlowPath({ fromX: leftX, toX: rightX, fromY, toY, height: h }),
      color: SANKEY_COLORS[f.from],
    });
  }
  return result;
}

/* Term pairs available for comparison (chronological, exactly 2 terms each) */
const PAIR_TERMS: Record<'t1t3' | 't3t4' | 't1t4', readonly SankeyTermKey[]> = {
  t1t3: ['t1', 't3'],
  t3t4: ['t3', 't4'],
  t1t4: ['t1', 't4'],
};
const PAIR_OPTIONS: ReadonlyArray<readonly [MobilePairKey, SankeyTermKey, SankeyTermKey]> = [
  ['t1t3', 't1', 't3'],
  ['t3t4', 't3', 't4'],
  ['t1t4', 't1', 't4'],
];

type MobilePairKey = 't1t3' | 't3t4' | 't1t4';

interface RiskTransitionSankeyProps {
  transitions: TransitionRow[];
  districtId?: string | null;
  schoolId?: string | null;
  classId?: string | null;
  onOpenClass?: (classId: string) => void;
  onOpenStudent?: (studentId: string) => void;
}

interface DrillFlowState {
  fromTerm: SankeyTermKey;
  toTerm: SankeyTermKey;
  fromRisk: BandKey;
  toRisk: BandKey;
}

// tooltip state no longer needed — replaced by inline hover strip

function RiskTransitionSankey({ transitions, schoolId, classId, onOpenClass, onOpenStudent }: RiskTransitionSankeyProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const { track } = useTracker();
  const [hovered, setHovered]         = React.useState<BuiltFlow | null>(null);
  const [hoverPos, setHoverPos]       = React.useState<{ x: number; y: number } | null>(null);
  // Cursor-follow tooltip replaces the old inline hover strip.
  const availableTerms = React.useMemo(() => {
    const present = (term: SankeyTermKey) => transitions.some((row) => {
      const risk = row[(term + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk'];
      const score = row[(term + 'CompositeScore') as 't1CompositeScore' | 't3CompositeScore' | 't4CompositeScore'];
      return score != null && risk && risk !== 'unknown' && risk !== 'more_info';
    });
    return (['t1','t3','t4'] as const).filter(present);
  }, [transitions]);
  // Desktop/tablet: full multi-term selection over available terms.
  const [activeTerms, setActiveTerms] = React.useState<SankeyTermKey[]>(availableTerms.length >= 2 ? availableTerms : ['t3','t4']);
  // Mobile-only: pair selection (one of three pairs)
  const [mobilePair, setMobilePair]   = React.useState<MobilePairKey>('t1t4');
  const [drillFlow, setDrillFlow]     = React.useState<DrillFlowState | null>(null);

  const resolvedActiveTerms = React.useMemo(() => {
    const filtered = activeTerms.filter((term) => availableTerms.includes(term));
    if (filtered.length >= 2) return filtered;
    return availableTerms.length >= 2 ? availableTerms : filtered;
  }, [activeTerms, availableTerms]);

  // Close modal on Escape key
  React.useEffect(() => {
    if (!drillFlow) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrillFlow(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drillFlow]);

  const effectiveShowUnknown = true;
  // Effective active terms: mobile uses the pair, desktop/tablet uses the multi-toggle
  const mobilePairTerms = PAIR_TERMS[mobilePair].filter((term) => availableTerms.includes(term));
  const effectiveActiveTerms: readonly SankeyTermKey[] = isMobile
    ? (mobilePairTerms.length >= 2 ? mobilePairTerms : availableTerms.slice(-2))
    : resolvedActiveTerms;
  const validRisk = (risk: RiskOrUnknown | undefined): boolean => !!risk && risk !== 'unknown' && risk !== 'more_info';
  const hasValidResult = (row: TransitionRow, term: SankeyTermKey): boolean => {
    const risk = row[(term + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk'] as RiskOrUnknown | undefined;
    const score = row[(term + 'CompositeScore') as 't1CompositeScore' | 't3CompositeScore' | 't4CompositeScore'];
    return score != null && validRisk(risk);
  };
  const comparisonTransitions = transitions.filter((row) => effectiveActiveTerms.every((term) => hasValidResult(row, term)));
  const excludedPartialCount = transitions.filter((row) => {
    const n = effectiveActiveTerms.filter((term) => hasValidResult(row, term)).length;
    return n > 0 && n < effectiveActiveTerms.length;
  }).length;

  const toggleTerm = (t: SankeyTermKey) => {
    setActiveTerms(prev => {
      if (prev.includes(t)) {
        if (prev.length <= 2) return prev;
        const next = prev.filter(x => x !== t);
        track("sankey_terms_changed", { mode: "desktop", active_terms: next.join(",") });
        return next;
      }
      const next = [...prev, t].sort() as SankeyTermKey[];
      track("sankey_terms_changed", { mode: "desktop", active_terms: next.join(",") });
      return next;
    });
  };

  const data = React.useMemo(() => {
    if (!comparisonTransitions || !comparisonTransitions.length) return null;
    const counts = (term: SankeyTermKey): BandCounts => {
      const c: BandCounts = { high: 0, moderate: 0, low: 0, unknown: 0 };
      comparisonTransitions.forEach(t => {
        const raw = t[(term + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk'] as RiskOrUnknown | undefined;
        const v: BandKey | undefined = raw === 'more_info' ? 'unknown' : raw;
        if (v && c[v] !== undefined) c[v]++;
      });
      if (!effectiveShowUnknown) c.unknown = 0;
      return c;
    };
    const flowsBetween = (tA: SankeyTermKey, tB: SankeyTermKey): RawFlow[] => {
      const map: Record<string, RawFlow> = {};
      comparisonTransitions.forEach(t => {
        const rawA = t[(tA + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk'] as RiskOrUnknown | undefined;
        const rawB = t[(tB + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk'] as RiskOrUnknown | undefined;
        const a: BandKey | undefined = rawA === 'more_info' ? 'unknown' : rawA;
        const b: BandKey | undefined = rawB === 'more_info' ? 'unknown' : rawB;
        if (!a || !b) return;
        if (!effectiveShowUnknown && (a === 'unknown' || b === 'unknown')) return;
        const k = `${a}__${b}__${tA}__${tB}`;
        const existing = map[k];
        if (!existing) map[k] = { from: a, to: b, n: 1, fromTerm: tA, toTerm: tB };
        else existing.n++;
      });
      return Object.values(map);
    };
    return {
      t1: counts('t1'), t3: counts('t3'), t4: counts('t4'),
      flows13: flowsBetween('t1','t3'),
      flows34: flowsBetween('t3','t4'),
      flows14: flowsBetween('t1','t4'),
    };
  }, [comparisonTransitions, effectiveShowUnknown]);

  if (!data) {
    const message = availableTerms.length < 2
      ? availableTerms.length === 1
        ? `This group has results in ${screeningPeriodList(availableTerms)} only, so there are no band changes to show.`
        : 'This group does not yet have scored results in enough screening windows to show band changes.'
      : `No students have classified results in every selected window (${screeningPeriodList(effectiveActiveTerms)}).`;
    return <div style={{ padding: 24, color: 'var(--fg-3)', fontSize: 13 }}>{message}</div>;
  }

  // ── Geometry: same logic as original; mobile is always 2-col via PAIR_TERMS ──
  const at = effectiveActiveTerms;
  const is3 = at.length === 3;
  const mode = is3 ? 't1t3t4'
    : (at.includes('t1') && at.includes('t3')) ? 't1t3'
    : (at.includes('t3') && at.includes('t4')) ? 't3t4'
    : 't1t4';

  const W = isMobile ? 400 : 800, H = isMobile ? 360 : 320, INNER_H = isMobile ? 300 : 260, GAP = 6, NODE_W = isMobile ? 16 : 14, TOP = 36;
  const BANDS_T1: readonly BandKey[]   = effectiveShowUnknown ? ['low','moderate','high','unknown'] : ['low','moderate','high'];
  const BANDS_REST: readonly BandKey[] = effectiveShowUnknown ? ['low','moderate','high','unknown'] : ['low','moderate','high'];

  const COL_X: Partial<Record<SankeyTermKey, number>> = isMobile
    ? (mode === 't1t3' ? { t1: 100, t3: 300 }
      : mode === 't3t4' ? { t3: 100, t4: 300 }
      : { t1: 100, t4: 300 })
    : is3
      ? { t1: 140, t3: 400, t4: 660 }
      : mode === 't1t3' ? { t1: 140, t3: 660 }
      : mode === 't3t4' ? { t3: 140, t4: 660 }
      : { t1: 140, t4: 660 };

  const off = (nodes: LayoutNode[]): LayoutNode[] => nodes.map(n => ({ ...n, y: n.y + TOP }));
  const nMap: Partial<Record<SankeyTermKey, LayoutNode[]>> = {};
  if (mode === 't1t3t4') {
    nMap.t1 = off(layoutNodes(data.t1, BANDS_T1,   INNER_H, GAP));
    nMap.t3 = off(layoutNodes(data.t3, BANDS_REST,  INNER_H, GAP));
    nMap.t4 = off(layoutNodes(data.t4, BANDS_REST,  INNER_H, GAP));
  } else if (mode === 't1t3') {
    nMap.t1 = off(layoutNodes(data.t1, BANDS_T1,   INNER_H, GAP));
    nMap.t3 = off(layoutNodes(data.t3, BANDS_REST,  INNER_H, GAP));
  } else if (mode === 't3t4') {
    nMap.t3 = off(layoutNodes(data.t3, BANDS_REST,  INNER_H, GAP));
    nMap.t4 = off(layoutNodes(data.t4, BANDS_REST,  INNER_H, GAP));
  } else {
    nMap.t1 = off(layoutNodes(data.t1, BANDS_T1,   INNER_H, GAP));
    nMap.t4 = off(layoutNodes(data.t4, BANDS_REST,  INNER_H, GAP));
  }

  const allFlows: BuiltFlow[] = [];
  if (mode === 't1t3t4') {
    allFlows.push(...buildFlows(nMap.t1 ?? [], nMap.t3 ?? [], data.flows13, (COL_X.t1 ?? 0) + NODE_W, COL_X.t3 ?? 0));
    allFlows.push(...buildFlows(nMap.t3 ?? [], nMap.t4 ?? [], data.flows34, (COL_X.t3 ?? 0) + NODE_W, COL_X.t4 ?? 0));
  } else if (mode === 't1t3') {
    allFlows.push(...buildFlows(nMap.t1 ?? [], nMap.t3 ?? [], data.flows13, (COL_X.t1 ?? 0) + NODE_W, COL_X.t3 ?? 0));
  } else if (mode === 't3t4') {
    allFlows.push(...buildFlows(nMap.t3 ?? [], nMap.t4 ?? [], data.flows34, (COL_X.t3 ?? 0) + NODE_W, COL_X.t4 ?? 0));
  } else {
    allFlows.push(...buildFlows(nMap.t1 ?? [], nMap.t4 ?? [], data.flows14, (COL_X.t1 ?? 0) + NODE_W, COL_X.t4 ?? 0));
  }

  const lastTermKey = at[at.length - 1] as SankeyTermKey;
  const studentNoun = (n: number) => n === 1 ? 'student' : 'students';
  const denominatorText = at.length === 2
    ? `Showing ${comparisonTransitions.length} ${studentNoun(comparisonTransitions.length)} with results in both ${screeningPeriodList(at)}.`
    : `Showing ${comparisonTransitions.length} ${studentNoun(comparisonTransitions.length)} with results in all ${at.length} selected screening windows.`;
  const exclusionText = excludedPartialCount > 0
    ? `${excludedPartialCount} ${studentNoun(excludedPartialCount)} without results in every selected window ${excludedPartialCount === 1 ? 'is' : 'are'} excluded.`
    : null;

  const handleEnter = (f: BuiltFlow, event: React.MouseEvent) => {
    setHovered(f);
    setHoverPos({ x: event.clientX, y: event.clientY });
  };
  const handleLeave = () => {
    setHovered(null);
    setHoverPos(null);
  };
  const handleMove  = (event: React.MouseEvent) => {
    setHoverPos({ x: event.clientX, y: event.clientY });
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Controls — desktop/tablet: original 3-toggle.
                     mobile: centred pair pill. */}
      {isMobile ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <div role="radiogroup" aria-label="Term pair to compare"
            style={{
              display: 'inline-flex',
              background: 'var(--ink-50)',
              borderRadius: 8,
              padding: 2,
              border: '1px solid var(--border-subtle)',
            }}>
            {PAIR_OPTIONS.filter(([, a, b]) => availableTerms.includes(a) && availableTerms.includes(b)).map(([id, fromTerm, toTerm]) => {
              const fromLabel = TERM_LABEL[fromTerm];
              const toLabel = TERM_LABEL[toTerm];
              const active = mobilePair === id;
              return (
                <button key={id} role="radio" aria-checked={active}
                  aria-label={`Compare ${fromLabel} to ${toLabel}`}
                  onClick={() => {
                    if (mobilePair === id) return;
                    track("sankey_terms_changed", { mode: "mobile", pair: id });
                    setMobilePair(id);
                  }}
                  style={{
                    border: 0,
                    background: active ? 'var(--ink-0)' : 'transparent',
                    boxShadow: active ? 'var(--sh-1)' : 'none',
                    padding: '6px 14px',
                    fontSize: 12,
                    fontWeight: 700,
                    color: active ? 'var(--fg-1)' : 'var(--fg-3)',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                    transition: 'background 120ms, color 120ms',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                  {fromLabel}
                  <ArrowRight aria-hidden size={11} strokeWidth={2.5} />
                  {toLabel}
                </button>
              );
            })}
          </div>
        </div>
      ) : availableTerms.length > 2 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {availableTerms.map(t => (
              <button key={t} onClick={() => toggleTerm(t)} style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                border: '1px solid var(--border-default)',
                background: at.includes(t) ? 'var(--enssa-aubergine)' : 'var(--ink-0)',
                color:      at.includes(t) ? 'var(--ink-0)' : 'var(--fg-2)',
              }}>{TERM_LABEL[t]}</button>
            ))}
          </div>
        </div>
      ) : null}

      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
        {/* Flows */}
        {allFlows.map(f => {
          const isHov = hovered && hovered.key === f.key;
          const opacity = hovered ? (isHov ? 0.82 : 0.1) : 0.45;
          return (
            <path key={f.key} d={f.path} opacity={opacity}
              style={{ fill: f.color, cursor: 'pointer', transition: 'opacity 150ms ease' }}
              onMouseEnter={(event) => handleEnter(f, event)}
              onMouseLeave={handleLeave}
              onMouseMove={handleMove}
              onClick={() => {
                track("sankey_flow_clicked", {
                  from_risk: f.from,
                  to_risk: f.to,
                  from_term: f.fromTerm.toUpperCase(),
                  to_term: f.toTerm.toUpperCase(),
                });
                setDrillFlow({ fromTerm: f.fromTerm, toTerm: f.toTerm, fromRisk: f.from, toRisk: f.to });
              }} />
          );
        })}
        {/* Node columns */}
        {(Object.entries(nMap) as Array<[SankeyTermKey, LayoutNode[]]>).map(([termKey, nodes], colIdx) => {
          const isFirst = colIdx === 0;
          const isLast  = termKey === lastTermKey;
          // A band is "new" if it didn't appear in ANY earlier column
          const earlierBands = new Set<BandKey>(
            (Object.entries(nMap) as Array<[SankeyTermKey, LayoutNode[]]>)
              .filter((_, i) => i < colIdx)
              .flatMap(([, ns]) => ns.map(n => n.band))
          );
          const x = COL_X[termKey] ?? 0;
          return (
            <g key={termKey}>
              <text x={x + NODE_W/2} y={TOP - 12} textAnchor="middle"
                fontSize={isMobile ? "13" : "10"} fontWeight="700" fill="var(--fg-3)" letterSpacing="1">
                {TERM_LABEL[termKey].toUpperCase()}
              </text>
              {nodes.map(n => {
                const isNew = !isFirst && !earlierBands.has(n.band);
                const labelFs = isMobile ? "13" : "11";
                const bandText = (b: RiskOrUnknown) =>
                  isMobile ? compactDomainRiskLabel(b) : domainRiskLabel(b);
                return (
                  <g key={n.band}>
                    <rect x={x} y={n.y} width={NODE_W} height={n.h} rx="2" style={{ fill: SANKEY_COLORS[n.band] }} />
                    {isFirst && !isLast && (
                      <text x={x - 8} y={n.y + n.h/2 + 4} textAnchor="end" fontSize={labelFs} fontWeight="600" fill="var(--fg-2)">
                        <tspan fontFamily="var(--font-mono)" fill="var(--fg-3)">{n.n}</tspan> {bandText(n.band)}
                      </text>
                    )}
                    {!isFirst && !isLast && (
                      <text x={x - 8} y={n.y + n.h/2 + 4} textAnchor="end" fontSize={labelFs} fontWeight="600" fill="var(--fg-2)">
                        <tspan fontFamily="var(--font-mono)" fill="var(--fg-3)">{n.n}</tspan>
                        {isNew && <tspan> {bandText(n.band)}</tspan>}
                      </text>
                    )}
                    {isLast && (
                      <text x={x + NODE_W + 8} y={n.y + n.h/2 + 4} textAnchor="start" fontSize={labelFs} fontWeight="600" fill="var(--fg-2)">
                        <tspan fontFamily="var(--font-mono)" fill="var(--fg-3)">{n.n}</tspan>
                        {isNew && <tspan> {bandText(n.band)}</tspan>}
                      </text>
                    )}
                    {isFirst && isLast && (
                      <text x={x - 8} y={n.y + n.h/2 + 4} textAnchor="end" fontSize={labelFs} fontWeight="600" fill="var(--fg-2)">
                        <tspan fontFamily="var(--font-mono)" fill="var(--fg-3)">{n.n}</tspan> {bandText(n.band)}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>

      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.45 }}>
        <div>{denominatorText}</div>
        {exclusionText && <div>{exclusionText}</div>}
      </div>

      {/* Drill-down overlay */}
      {drillFlow && (() => {
        const { fromTerm, toTerm, fromRisk, toRisk } = drillFlow;
        const fromField = (fromTerm + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk';
        const toField   = (toTerm   + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk';
        const matching  = transitions.filter(s => s[fromField] === fromRisk && s[toField] === toRisk);

        // Determine drill level
        let drillContent: React.ReactNode;
        if (classId) {
          // Class level: list individual students with score change T1→latest term
          const lastTerm  = toTerm;
          const firstTerm = fromTerm;

          // Read the real per-term standard score from the assessment data.
          function scoreForTerm(s: TransitionRow, term: SankeyTermKey): number | null {
            const field = (term + 'CompositeScore') as 't1CompositeScore' | 't3CompositeScore' | 't4CompositeScore';
            const v = s[field];
            return v ?? s.compositeScore;
          }

          drillContent = (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left',  padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--ink-50)' }}>Student</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--ink-50)' }}>ENSSA standard score</th>
                  </tr>
                </thead>
                <tbody>
                  {matching.map(s => {
                    const s1 = scoreForTerm(s, firstTerm);
                    const s2 = scoreForTerm(s, lastTerm);
                    const delta = (s2 ?? 0) - (s1 ?? 0);
                    const s1Unknown = s[(firstTerm + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk'] === 'unknown';
                    const s2Unknown = s[(lastTerm  + 'Risk') as 't1Risk' | 't3Risk' | 't4Risk'] === 'unknown';
                    const hasUnknown = s1Unknown || s2Unknown;
                    return (
                      <tr key={s.studentId} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                          {onOpenStudent
                            ? (
                              <button
                                type="button"
                                onClick={() => onOpenStudent(s.studentId)}
                                style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--link)', cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2 }}
                              >
                                {s.studentName}
                              </button>
                            )
                            : s.studentName}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {hasUnknown ? (
                              <>
                                {s1Unknown
                                  ? <span style={{ color: 'var(--fg-3)', fontWeight: 700 }}>?</span>
                                  : <span style={{ fontWeight: 700 }}>{s1}</span>}
                                <ArrowRight aria-hidden size={12} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
                                {s2Unknown
                                  ? <span style={{ color: 'var(--fg-3)', fontWeight: 700 }}>?</span>
                                  : <span style={{ fontWeight: 700 }}>{s2}</span>}
                              </>
                            ) : (
                              <>
                                <span style={{ fontWeight: 700 }}>{s1}</span>
                                <ArrowRight aria-hidden size={12} style={{ color: 'var(--fg-3)', flexShrink: 0 }} />
                                <span style={{ fontWeight: 700 }}>{s2}</span>
                                <span style={{ fontSize: 12, fontWeight: 700, marginLeft: 2, color: delta > 0 ? 'var(--risk-on-track-fg)' : delta < 0 ? 'var(--risk-concern-fg)' : 'var(--fg-3)' }}>
                                  ({delta > 0 ? '+' : ''}{delta})
                                </span>
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        } else if (schoolId) {
          // School level: breakdown by class, grouped by year level
          const byYear: Record<string, Record<string, number>> = {};
          matching.forEach(s => {
            const yr = s.year || 'Unknown';
            const yearGroup = byYear[yr] ?? (byYear[yr] = {});
            const cl = s.classId || 'Unknown';
            yearGroup[cl] = (yearGroup[cl] ?? 0) + 1;
          });
          drillContent = (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {Object.entries(byYear).sort().map(([yr, classes]) => (
                <div key={yr}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    {yr === 'F' ? 'Foundation' : yr === 'Y1' ? 'Year 1' : yr}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--ink-50)' }}>Class</th>
                        <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--ink-50)' }}>Students</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(classes).sort((a,b) => b[1]-a[1]).map(([cl, n]) => (
                        <tr key={cl} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                            {onOpenClass ? (
                              <button onClick={() => onOpenClass(cl)}
                                style={{ background: 'none', border: 0, padding: 0, color: 'var(--risk-on-track)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                                Class {cl}
                              </button>
                            ) : `Class ${cl}`}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                            {n} <span style={{ color: 'var(--fg-3)', fontWeight: 400 }}>({Math.round(n/matching.length*100)}%)</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          );
        } else {
          // District level: breakdown by school
          const bySchool: Record<string, number> = {};
          matching.forEach(s => {
            const k = s.schoolName || s.schoolId || 'Unknown';
            bySchool[k] = (bySchool[k] ?? 0) + 1;
          });
          drillContent = (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--ink-50)' }}>School</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', borderBottom: '1px solid var(--border-subtle)', background: 'var(--ink-50)' }}>Students</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(bySchool).sort((a,b) => b[1]-a[1]).map(([name, n]) => (
                  <tr key={name} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>{name}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                      {n} <span style={{ color: 'var(--fg-3)', fontWeight: 400 }}>({Math.round(n/matching.length*100)}%)</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        }

        return (
          <div
            onClick={() => setDrillFlow(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
              zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16,
            }}>
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--ink-0)', borderRadius: 12, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                maxWidth: 'min(480px, calc(100vw - 32px))', width: '100%', maxHeight: '80vh', overflow: 'auto',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fg-3)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {TERM_LABEL[fromTerm]}
                    <ArrowRight aria-hidden size={11} strokeWidth={2.5} />
                    {TERM_LABEL[toTerm]}
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span><span style={{ color: SANKEY_COLORS[fromRisk] }}>●</span> {domainRiskLabel(fromRisk)}</span>
                    <ArrowRight aria-hidden size={14} style={{ color: 'var(--fg-3)' }} />
                    <span><span style={{ color: SANKEY_COLORS[toRisk] }}>●</span> {domainRiskLabel(toRisk)}</span>
                  </h3>
                  <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                    {matching.length} student{matching.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <button onClick={() => setDrillFlow(null)}
                  aria-label="Close"
                  style={{ background: 'var(--ink-100)', border: '1px solid var(--border-default)', borderRadius: 6, width: 44, height: 44, cursor: 'pointer', color: 'var(--fg-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <X aria-hidden size={18} />
                </button>
              </div>
              <div style={{ padding: '12px 0' }}>{drillContent}</div>
            </div>
          </div>
        );
      })()}

      {/* Cursor-follow hover tooltip. Mobile uses tap/drill instead. */}
      {!isMobile && hovered && hoverPos && (
        <div
          style={{
            position: 'fixed',
            left: hoverPos.x + 14,
            top: hoverPos.y + 14,
            zIndex: 60,
            pointerEvents: 'none',
            background: 'var(--ink-900)',
            color: '#fff',
            borderRadius: 8,
            padding: '8px 10px',
            boxShadow: 'var(--sh-2)',
            fontSize: 12,
            lineHeight: 1.35,
            maxWidth: 320,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 16px auto', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.78 }}>
                {TERM_LABEL[hovered.fromTerm]}
              </div>
              <div style={{ marginTop: 3, fontWeight: 700 }}>
                <span style={{ color: SANKEY_COLORS[hovered.from] }}>●</span> {domainRiskLabel(hovered.from)}
              </div>
            </div>
            <ArrowRight aria-hidden size={14} style={{ alignSelf: 'end', marginBottom: 2, opacity: 0.75 }} />
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.78 }}>
                {TERM_LABEL[hovered.toTerm]}
              </div>
              <div style={{ marginTop: 3, fontWeight: 700 }}>
                <span style={{ color: SANKEY_COLORS[hovered.to] }}>●</span> {domainRiskLabel(hovered.to)}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 4, opacity: 0.78 }}>
            {hovered.n} student{hovered.n !== 1 ? 's' : ''} · click to expand
          </div>
        </div>
      )}
    </div>
  );
}

export { RiskTransitionSankey };
